import {dehydrate, DehydratedState, QueryClient, useQuery} from "@tanstack/react-query";
import {GetServerSideProps} from "next";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {useRouter} from "next/router";
import {ParsedUrlQuery} from "querystring";
import {ReactElement, useContext, useEffect, useState} from "react";
import {ClipLoader} from "react-spinners";
import ImmobilieKaufen from "../../components/FindImmobilieForm/ImmobilieKaufen";
import ImmoCard from "../../components/common/ImmoCard";
import Layout from "../../components/common/Layout";
import {MobileMenuState, PwaContext} from "../../components/providers/PwaContext";
import {getSequelizeItemsByQueryParams} from "../../data/ObjectsDataHelper";
import {fetchItem} from "../../data/ReactQueries";
import {enforceArray, handleSort, replaceRouterQueryParamsShallow} from "../../helpers/FilterFunctions";
import VerkaufSidebar from "../../components/VerkaufSidebar";

import {Filter} from "../../data/Filter/Filter.config";
import VerkaufMobileFilterMenu from "../../components/VerkaufMobileFilterMenu";
import CustomListbox from "../../components/common/CustomListbox";
import {VerkaufSortingOptions} from "../../data/Filter/VerkaufFilter.config";
import HeaderImageWithHeading from "../../components/common/HeaderImageWithHeading";
import {ObjectInterfaceWithCity} from "../../interfaces/ObjectInterface";
import {useTranslation} from "next-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import IconArrowDown from "../../data/Icons/leon-inmobiliaria-icons-arrow-1-down.svg";
import {getSafeLocale} from "../../helpers/LocalizeObject";

interface Vermietung {
	initialQuery: ParsedUrlQuery;
	objects: ObjectInterfaceWithCity[];
	dehydratedState: DehydratedState;
}

export default function Verkauf(props: Vermietung) {
	const [noSearchResults, setNoSearchResults] = useState(false);
	const {mobileMenuState, setMobileMenuState} = useContext(PwaContext);
	const pageSize = 9;
	const [totalPages, setTotalPages] = useState<number>();
	const {t} = useTranslation();
	const router = useRouter();
	const [totalLoadedObjectItems, setTotalLoadedObjectItems] = useState<
		ObjectInterfaceWithCity[]
	>([]);
	const [currPage, setCurrPage] = useState(
		parseInt((router.query.page as string) ?? "1")
	);
	const {
		data: objects,
		isLoading: objectsLoading,
		refetch
	} = useQuery(
		[
			"objects",
			{...router.query, [Filter.Sale.queryKey]: 1, locale: router.locale}
		],
		() => {
			return fetchItem(
				"objects",
				{
					...router.query,
					[Filter.Sale.queryKey]: 1
				},
				router.locale
			);
		}
	);

	useEffect(() => {
		if (router.isReady && router.query) {
			setCurrPage(1);
			fetchNewPage(1);
		}
	}, []);

	useEffect(() => {
		if (objects) setTotalPages(Math.ceil(objects.totalItems / pageSize));
	}, [objects]);

	useEffect(() => {
		if (objects) {
			if (totalLoadedObjectItems) {
				const newTotalLoadedObjectItems = totalLoadedObjectItems.concat(
					objects.items
				);
				const newTotalLoadedObjectItemsDeduplicated =
					newTotalLoadedObjectItems.filter(
						(item: ObjectInterfaceWithCity, pos: number) =>
							newTotalLoadedObjectItems.findIndex(
								(_item: ObjectInterfaceWithCity) => _item.id === item.id
							) === pos
					);
				setTotalLoadedObjectItems(newTotalLoadedObjectItemsDeduplicated);
			} else {
				setTotalLoadedObjectItems(objects.items);
			}
		}
	}, [objects]);

	useEffect(() => {
		if (!router.query.page) {
			setTotalLoadedObjectItems([]);
			setCurrPage(1);
			fetchNewPage(1);
		}
	}, [router.query.page]);

	function handleNext() {
		if (totalPages && currPage < totalPages) {
			fetchNewPage(currPage + 1);
			setCurrPage(currPage + 1);
		}
	}

	function fetchNewPage(newPage: number) {
		let queryParams = router.query;
		queryParams = {...queryParams, page: newPage.toString()};
		replaceRouterQueryParamsShallow(router, queryParams, true);
	}

	useEffect(() => {
		if (objects) {
			if (objects.items.length === 0) {
				setNoSearchResults(true);
			} else {
				setNoSearchResults(false);
			}
		}
	}, [objects]);

	useEffect(() => {
		if (mobileMenuState === MobileMenuState.FILTER) {
			document
				.getElementById("search-result-container")
				?.scrollIntoView({behavior: "smooth"});
		}
	}, [mobileMenuState]);

	return (
		<>
			<HeaderImageWithHeading
				imageSrc={
					"/images/verkauf_header2.jpg"
				}
				title=""
				subtitle={t("pages.verkauf.subHeading")}
			/>
			<div className="container mx-auto relative">
				<div className="lg:w-11/12 xl:w-10/12 ml-auto mr-auto lg:-mt-10">
					<ImmobilieKaufen/>
				</div>
			</div>
			<div
				className="container mt-24 w-auto mx-10 sm:mx-auto sm:px-6 xl:px-0"
				id="search-result-container"
			>
				<div className="flex flex-col justify-between md:flex-row gap-4">
					<h3 className="min-w-[390px]">
						{t("pages.verkauf.results")} (
						{(objects && objects.totalItems) ?? <ClipLoader size={22}/>})
					</h3>
					{!noSearchResults && (
						<>
							<div className="w-[190px]"></div>
							<div className="w-44 hidden xl:block">
                <span className="input-caption">
                  {t("pages.verkauf.sortBy")}
                </span>
								<CustomListbox
									singleChoice
									noCheckBox
									defaultChecked={enforceArray({
										name: t(
											VerkaufSortingOptions.find(
												(o) =>
													o.sortDirection &&
													router.query.sort?.includes(o.sortDirection)
											)?.label ?? VerkaufSortingOptions[0].label
										)
									})}
									items={VerkaufSortingOptions.map((i) => {
										return {name: t(i.label)};
									})}
									onSelectItems={(selected) =>
										handleSort(
											selected,
											VerkaufSortingOptions.map((o) => {
												return {...o, label: t(o.label)};
											}),
											router
										)
									}
								/>
							</div>
						</>
					)}
				</div>
				<div className="flex gap-4 my-8">
					<VerkaufSidebar/>
					<div className="flex flex-col items-center w-full">
						{!objectsLoading && objects.totalItems === 0 ? (
							<h4 className="text-center w-full">
								{t("pages.verkauf.noResults")}
							</h4>
						) : (
							<InfiniteScroll
								next={handleNext}
								hasMore={totalPages ? currPage < totalPages : false}
								style={{overflow: "hidden"}}
								loader={
									<div className="w-full items justify-center flex">
										<ClipLoader size={250}/>
									</div>
								}
								dataLength={totalLoadedObjectItems.length}
							>
								<div
									className={`flex w-full flex-wrap gap-4 md:justify-between justify-center grow items-center`}
								>
									{totalLoadedObjectItems.map(
										(object: ObjectInterfaceWithCity) => {
											return (
												<ImmoCard sale key={object["id"]} object={object}/>
											);
										}
									)}
								</div>
							</InfiniteScroll>
						)}
					</div>
				</div>
			</div>
			<VerkaufMobileFilterMenu
				open={mobileMenuState === MobileMenuState.FILTER}
				onClose={() => setMobileMenuState(MobileMenuState.CLOSED)}
			/>
			<button
				className="fixed right-4 bottom-16 md:bottom-4 bg-leo-button-gray h-14 w-14 text-white rotate-180"
				onClick={() =>
					document
						.getElementById("search-result-container")
						?.scrollIntoView({behavior: "smooth"})
				}
			>
				<IconArrowDown/>
			</button>
		</>
	);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const queryClient = new QueryClient();
	const tmpQuery = {...context.query, [Filter.Sale.queryKey]: "1"};
	const objects = await getSequelizeItemsByQueryParams(
		tmpQuery,
		"objects",
		getSafeLocale(context.locale)
	);

	queryClient.setQueryData(
		[
			"objects",
			{...context.query, [Filter.Sale.queryKey]: 1, locale: getSafeLocale(context.locale)}
		],
		objects
	);
	return {
		props: {
			dehydratedState: dehydrate(queryClient),
			objects: objects,
			initialQuery: context.query,
			...(await serverSideTranslations(getSafeLocale(context.locale) as string, ["common"]))
		}
	};
};

Verkauf.getLayout = function getLayout(page: ReactElement) {
	return (
		<Layout showFilterBar={true} headerNotTransparent={false}>
			<div className="verkauf-list">
				{page}
			</div>
		</Layout>
	);
};
